// src/components/onboarding/setupActions.js
//
// The writes the guided setup performs, kept apart from the screens that
// collect the details.
//
// Each one creates exactly what the equivalent page creates — same collection,
// same field names, same defaults — so a company made here is indistinguishable
// from one made in Management → Companies. Anything else and a brand new tenant
// would start life with records subtly unlike every record made afterwards,
// which is the kind of difference that surfaces months later as "why does this
// one load behave strangely".
//
// What the setup adds on top is the wiring. Doing this by hand means visiting
// four pages and knowing that a truck belongs to a company, a driver to both a
// company and a truck, and a load to a driver. That knowledge is the real
// barrier on somebody's first day, so it is done for them.

import { collection, addDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../firebase';
import { logAudit } from '../../utils/auditLog';

/** What this tenant hauls. Drives which fields appear across the whole app. */
export async function saveCommodityTypes({ tenantId, commodityTypes, user }) {
  if (!tenantId || !commodityTypes?.length) return;

  await setDoc(doc(db, 'tenantSettings', tenantId), {
    commodityTypes,
    commodityTypesUpdatedAt: serverTimestamp(),
    commodityTypesUpdatedBy: user?.uid || null,
    tenantId,
  }, { merge: true });
}

/**
 * The tenant's first company — its MC/DOT authority.
 *
 * Created as a parent, never a subdivision: subdivisions hang off a parent and
 * there is nothing to hang off yet.
 */
export async function createCompany({ tenantId, user, form }) {
  const payload = {
    name: form.name.trim(),
    address: form.address.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    mcNumber: (form.mcNumber || '').trim(),
    usdot: (form.usdot || '').trim(),
    taxId: '',
    logoPath: '',
    logoUrl: '',
    active: true,
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    type: 'parent',
    parentCompanyId: null,
    parentCompanyName: null,
  };

  const ref = await addDoc(collection(db, 'companies'), payload);

  await logAudit({
    userId: user?.uid,
    userEmail: user?.email,
    action: 'COMPANY_CREATED',
    targetType: 'company',
    targetId: ref.id,
    details: {
      companyName: payload.name,
      usdot: payload.usdot,
      mcNumber: payload.mcNumber,
      viaGuidedSetup: true,
    },
    tenantId,
  });

  return { id: ref.id, ...payload };
}

/**
 * The first truck.
 *
 * Deliberately Inactive, exactly as the Trucks page creates one. Truck status
 * drives the subscription, and a setup wizard must never quietly add to
 * somebody's bill on their first morning. They switch it to Active when they
 * are ready, which the Trucks page already explains.
 */
export async function createTruck({ tenantId, user, form, company }) {
  const payload = {
    unitNumber: form.unitNumber.trim(),
    yearMake: (form.yearMake || '').trim(),
    color: '',
    vin: '',
    axleCount: form.axleCount ? Number(form.axleCount) : null,
    vehicleCapacity: form.vehicleCapacity ? Number(form.vehicleCapacity) : null,
    type: form.type || 'Company Truck',
    subType: form.type === 'Company Truck' ? (form.subType || 'Financed/Owned') : '',
    isOwnerDriver: form.type === 'Owner Operator',
    paymentPerWeek: '',
    hasTrailer: false,
    trailerUnitNumber: '',
    trailerPaymentPerWeek: '',
    ownedBy: '',
    attachedTrailer: '',
    assignedCompanyId: company?.id || '',
    assignedCompanyName: company?.name || '',
    parentCompanyId: company?.id || null,
    parentCompanyName: company?.name || null,
    fuelCard: '',
    tollDevice: '',
    status: 'Inactive',
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'trucks'), payload);

  await logAudit({
    userId: user?.uid,
    userEmail: user?.email,
    action: 'TRUCK_CREATED',
    targetType: 'truck',
    targetId: ref.id,
    details: { unitNumber: payload.unitNumber, viaGuidedSetup: true },
    tenantId,
  });

  return { id: ref.id, ...payload };
}

/**
 * The first driver, wired to the company and the truck in one go.
 *
 * Both company fields are set. They are genuinely different things —
 * `assignedCompanyId` is the authority whose name goes on the BOL,
 * `parentCompanyId` is who manages the driver — and they are allowed to differ
 * later. On day one, with one company, they are the same, and leaving either
 * blank is what makes a driver invisible on half the screens.
 */
export async function createDriver({ tenantId, user, form, company, truck }) {
  const payload = {
    name: form.name.trim(),
    email: (form.email || '').trim(),
    phone: (form.phone || '').trim(),
    assignedCompanyId: company?.id || '',
    assignedCompanyName: company?.name || '',
    parentCompanyId: company?.id || null,
    parentCompanyName: company?.name || null,
    status: 'Active',
    assignedTruckId: truck?.id || '',
    assignedTruckUnit: truck?.unitNumber || '',
    licenseNumber: '',
    licenseState: '',
    licenseExp: '',
    medicalExp: '',
    notes: '',
    paymentType: form.paymentType || 'percentage',
    paymentRate: form.paymentRate ? parseFloat(form.paymentRate) : 0,
    driverType: truck?.type === 'Owner Operator' ? 'Owner Operator' : 'Company Driver',
    depositAmount: 0,
    depositPaid: 0,
    depositWeeklyIncrement: 0,
    showOnBOL: false,
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'drivers'), payload);

  await logAudit({
    userId: user?.uid,
    userEmail: user?.email,
    action: 'DRIVER_CREATED',
    targetType: 'driver',
    targetId: ref.id,
    details: { driverName: payload.name, viaGuidedSetup: true },
    tenantId,
  });

  return { id: ref.id, ...payload };
}

/**
 * Invite the office: dispatchers, accountants, fleet.
 *
 * Nothing is emailed. The existing invite flow produces a signup link that the
 * inviter sends themselves, and this keeps that exactly as it is — inventing a
 * second mechanism that actually sends mail would mean two different ways to
 * join a tenant. The links come back together so they can be copied in one go
 * rather than through one browser prompt at a time.
 */
export async function createInvites({ tenantId, user, invites, company }) {
  const created = [];

  for (const invite of invites) {
    const email = (invite.email || '').trim().toLowerCase();
    if (!email) continue;

    const token = uuidv4();

    await addDoc(collection(db, 'pendingInvites'), {
      name: (invite.name || '').trim() || email.split('@')[0],
      email,
      role: [invite.role],
      company: company?.name || '',
      active: true,
      tenantId,
      token,
      invitedBy: user?.email || 'System',
      invitedAt: serverTimestamp(),
      status: 'pending',
    });

    created.push({
      email,
      role: invite.role,
      link: `https://loadmemo.com/finishSignUp?token=${token}&tenant=${tenantId}`,
    });

    await logAudit({
      userId: user?.uid,
      userEmail: user?.email,
      action: 'USER_INVITE_INITIATED',
      targetType: 'user_invite',
      targetId: email,
      details: { invitedUserEmail: email, roleAssigned: [invite.role], viaGuidedSetup: true },
      tenantId,
    });
  }

  return created;
}

// A real lane, so the map, the mileage and the route resolve to something
// sensible rather than sitting empty on the new user's first look at the app.
const DEMO_LANE = {
  pickupLocation: '145 Auction Ln, Statesville, NC 28625',
  pickupLocationName: 'Statesville Auto Auction',
  deliveryLocation: '5827 Belair Rd, Baltimore, MD 21206',
  deliveryLocationName: 'Baltimore Motors',
};

const inDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(9, 0, 0, 0);
  // Local time in the datetime-local format the load form uses. Built by hand
  // rather than with toISOString, which would shift it into UTC and show the
  // wrong day to anyone west of Greenwich.
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * A sample load, so a new tenant sees a populated app instead of five empty
 * screens.
 *
 * Marked `isDemo` and given a DEMO load number, because the alternative is a
 * fictional $950 sitting in their revenue, on their dashboard and in their
 * first settlement — and by the time anybody notices, nobody remembers where it
 * came from. The finish screen offers to delete it, and the flag is what makes
 * finding it again possible.
 *
 * Mileage is left blank on purpose: the geocodeLoadAddresses trigger fills it
 * from these addresses within seconds, which also shows the new user that it
 * happens by itself.
 */
export async function createDemoLoad({ tenantId, user, company, driver, truck, isAutoHauling }) {
  const payload = {
    load_id: 'DEMO-0001',
    isDemo: true,
    companyName: company?.name || '',
    companyId: company?.id || null,
    ...DEMO_LANE,
    pickupDateTime: inDays(1),
    deliveryDateTime: inDays(3),
    amount: '950',
    mileage: '',
    brokerId: '',
    brokerName: 'Sample Broker LLC',
    driverId: driver?.id || '',
    truckId: truck?.id || '',
    dispatcherId: '',
    status: 'Booked',
    pickupInstructions: 'Sample load created during setup. Delete it whenever you like.',
    deliveryInstructions: '',
    adminNotes: '',
    actualPU: null,
    actualDEL: null,
    driverCollectionAmount: '',
    brokerFeeCollection: '',
    factoringFee: '',
    storageFee: '',
    collectionInstructions: '',
    paymentMethod: '',
    paymentTerms: '',
    vehicles: isAutoHauling
      ? [{ make: 'Jeep', model: 'Compass', year: '2025', vin: '3C4NJDBN1ST603466', inop: false }]
      : [{ make: '', model: '', year: '', vin: '', inop: false }],
    vehicleCount: 1,
    sourceType: 'guided_setup',
    tenantId,
    createdBy: user?.uid || null,
    createdByEmail: user?.email || null,
    createdAt: serverTimestamp(),
    isArchived: false,
    dispatchDocuments: [],
    gatePassDocuments: [],
    parentCompanyId: company?.id || null,
    parentCompanyName: company?.name || null,
  };

  const ref = await addDoc(collection(db, 'loads'), payload);

  await logAudit({
    userId: user?.uid,
    userEmail: user?.email,
    action: 'add_load',
    targetType: 'load',
    targetId: ref.id,
    details: { load_id: payload.load_id, demo: true, viaGuidedSetup: true },
    tenantId,
  });

  return { id: ref.id, ...payload };
}
